<script setup lang="ts">
import VoiceOver from "@models/VoiceOver";
import { Action } from "@models/ActionHierarchy";
import { useCachedTimeBasedTimeline } from "@modules/cached_time_based_timeline";
import { formatSecondsAsString } from "@modules/helpers";
import PublicPlayerTimelineItem from "@timeline/PublicPlayerTimelineItem.vue";

const props = defineProps<{
  currentActionId?: string;
  selectedActionIds?: string[];
  selectedVoiceOverIds?: string[];
  actions: Action[];
  voiceOvers: VoiceOver[];
  videoRuntime: number;
  videoDimensions: { width: number; height: number };
  baseFileUrl: string;
  logoUrl?: string | undefined;
}>();

const emit = defineEmits<{
  (e: "jump-to-action-id", id: string): void;
  (e: "select-voice-over-id", id: string): void;
}>();

function selectActionId(id) {
  emit("jump-to-action-id", id);
}

const { groups, getGroupWidthPercentage } = useCachedTimeBasedTimeline(props);

function calculateStartAt(actionId): number {
  const allGroups = groups.value;
  let startAt = 0;
  for (const group of allGroups) {
    for (const action of group.actions) {
      if (action.id === actionId) {
        return startAt;
      }
      startAt += action.duration;
    }
  }
  return startAt;
}
</script>

<template>
  <div class="flex select-none">
    <div class="relative grow pb-px">
      <ol class="flex">
        <li
          v-for="group in groups"
          :key="`group-${group.id}`"
          class="flex flex-wrap"
          :style="{ width: getGroupWidthPercentage(group.duration) }"
        >
          <PublicPlayerTimelineItem
            v-for="action in group.actions"
            :key="action.id"
            :style="{
              'flex-grow': action.duration,
            }"
            :in-the-past="action.inThePast"
            :is-current="action.current"
            :align-hover="action.alignHover"
            :screenshot-url="action.screenshotUrl"
            :slide="action.slide"
            :description="action.description"
            :action-id="action.id"
            :starts-at="formatSecondsAsString(calculateStartAt(action.id))"
            @click.capture="selectActionId(action.id)"
          />
        </li>
      </ol>
    </div>
  </div>
</template>
